import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorMessage - The detailed discount error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorMessage'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const eventTarget = event.target;
    const form =
      eventTarget instanceof HTMLFormElement
        ? eventTarget
        : eventTarget instanceof HTMLElement
          ? eventTarget.closest('form')
          : null;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    const submitButton = form.querySelector('.cart-discount__button');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) {
      this.#showError('Please enter a discount code.');
      discountCode.focus();
      return;
    }

    if (submitButton instanceof HTMLButtonElement && submitButton.disabled) return;

    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
    }

    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => code.toLocaleLowerCase() === discountCodeValue.toLocaleLowerCase())) {
        this.#showError(`Discount code “${discountCodeValue}” has already been applied.`);
        return;
      }

      this.#clearErrors();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(this.#getResponseError(data, `Unable to apply the discount code (HTTP ${response.status}).`));
      }
      const returnedDiscounts = Array.isArray(data.discount_codes) ? data.discount_codes : [];
      const returnedDiscount = returnedDiscounts.find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
          discount.code?.toLocaleLowerCase() === discountCodeValue.toLocaleLowerCase()
      );

      if (!returnedDiscount || returnedDiscount.applicable === false) {
        discountCode.value = '';
        this.#showError(
          this.#getResponseError(
            data,
            `Discount code “${discountCodeValue}” is invalid, expired, or not applicable to the items in this cart.`
          )
        );
        return;
      }

      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') throw new Error('Discount update did not return the cart section');

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          returnedDiscount.applicable === true
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('.cart-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Unable to apply cart discount', error);
      this.#showError(error instanceof Error ? error.message : 'Unable to apply the discount code. Please try again.');
    } finally {
      this.#activeFetch = null;
      if (submitButton instanceof HTMLButtonElement) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const removeButton = event.target.closest('.cart-discount__pill-remove');
    if (!(removeButton instanceof HTMLButtonElement) || removeButton.disabled) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    removeButton.disabled = true;
    removeButton.setAttribute('aria-busy', 'true');
    pill.setAttribute('aria-busy', 'true');

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(this.#getResponseError(data, `Unable to remove the discount code (HTTP ${response.status}).`));
      }
      const newHtml = data.sections?.[this.dataset.sectionId];
      if (typeof newHtml !== 'string') throw new Error('Discount removal did not return the cart section');

      await morphSection(this.dataset.sectionId, newHtml, {
        mode: this.closest('.cart-drawer') ? 'hydration' : 'full',
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.error('Unable to remove cart discount', error);
      this.#showError(error instanceof Error ? error.message : 'Unable to remove the discount code. Please try again.');
    } finally {
      this.#activeFetch = null;
      if (removeButton.isConnected) {
        removeButton.disabled = false;
        removeButton.removeAttribute('aria-busy');
        pill.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    this.#showError(
      type === 'shipping'
        ? 'This shipping discount will be applied at checkout after you enter a delivery address.'
        : 'This discount code cannot be applied to your cart.'
    );
  }

  /** @param {string} message */
  #showError(message) {
    const { cartDiscountError, cartDiscountErrorMessage } = this.refs;
    cartDiscountErrorMessage.textContent = message;
    cartDiscountErrorMessage.classList.remove('hidden');
    cartDiscountError.classList.remove('hidden');
  }

  #clearErrors() {
    const { cartDiscountError, cartDiscountErrorMessage } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorMessage.classList.add('hidden');
    cartDiscountErrorMessage.textContent = '';
  }

  /**
   * @param {any} data
   * @param {string} fallback
   */
  #getResponseError(data, fallback) {
    const errors = data?.errors;
    if (typeof errors === 'string' && errors.trim()) return errors;
    if (errors && typeof errors === 'object') {
      const firstError = Object.values(errors).flat().find((value) => typeof value === 'string' && value.trim());
      if (typeof firstError === 'string') return firstError;
    }
    if (typeof data?.description === 'string' && data.description.trim()) return data.description;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    return fallback;
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-form-component')) {
  customElements.define('cart-discount-form-component', CartDiscount);
}
